import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PiCards, PiCheckCircle, PiCheckCircleFill } from "react-icons/pi";
import { lstr } from "./localization";
import { useStoryPracticeCountQuery } from "./practiceApi";
import { StoryDescriptor } from "./story";
import { StoryRow, StoryRows } from "./StoryList";
import { Pup } from "./Pup";
import { Confetti } from "./Confetti";
import { StoryRating } from "./StoryRating";

// StoryEnd closes a story: a small celebration, the read mark, and what to
// read or do next, so finishing a story leads straight into the next one.
// Signed-in readers can rate the story at the very end.
export function StoryEnd({
  l,
  r,
  loggedIn,
  read,
  onSetRead,
  readError,
  upNext,
  progressByStoryId,
}: {
  l: string;
  r: string;
  loggedIn: boolean;
  // The server-side read mark; only meaningful when logged in.
  read: boolean;
  onSetRead: (read: boolean) => void;
  readError: boolean;
  upNext: StoryDescriptor[];
  progressByStoryId: Map<string, number>;
}) {
  const strings = lstr(l);
  // Confetti only for marking the story read here, not for a story that
  // was already marked when it was opened.
  const [celebrating, setCelebrating] = useState(false);
  // The story being read, from its route.
  const { storyId = "" } = useParams();
  const storyPracticeCount =
    useStoryPracticeCountQuery(r, storyId, loggedIn && storyId !== "").data ??
    0;

  return (
    <section className="mt-16 flex flex-col items-center pb-8 text-center">
      <div aria-hidden="true" className="flex items-center gap-3 text-border">
        <span className="h-px w-12 bg-border" />
        <span className="font-literata text-lg text-secondary-text">❦</span>
        <span className="h-px w-12 bg-border" />
      </div>
      <div className="relative mt-8">
        <Pup
          shape="circle"
          mood={read || !loggedIn ? "happy" : "reading"}
          className="h-20 w-20"
        />
        {celebrating && <Confetti />}
      </div>
      <h2 className="mt-4 font-literata text-2xl font-bold text-main-text">
        {strings.story_end_title}
      </h2>
      <p className="mt-1 text-secondary-text">{strings.story_end_congrats}</p>

      {loggedIn ? (
        read ? (
          <div className="mt-5 flex flex-col items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-light px-4 py-2 text-sm font-semibold text-primary">
              <PiCheckCircleFill size={18} />
              {strings.story_end_read_done}
            </span>
            <button
              type="button"
              onClick={() => {
                setCelebrating(false);
                onSetRead(false);
              }}
              className="text-xs text-secondary-text underline-offset-2 hover:text-main-text hover:underline"
            >
              {strings.mark_story_unread_button_label}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setCelebrating(true);
              onSetRead(true);
            }}
            className="btn-primary mt-5 px-6 py-3 text-[15px]"
          >
            <PiCheckCircle size={19} />
            {strings.mark_story_read_button_label}
          </button>
        )
      ) : (
        <div className="card mt-6 flex max-w-sm flex-col items-center gap-3 px-6 py-5">
          <p className="text-sm text-main-text">
            {strings.story_end_signup_text}
          </p>
          <Link to="/signup" className="btn-primary px-5 py-2.5 text-sm">
            {strings.home_cta_button}
          </Link>
        </div>
      )}
      {readError && (
        <p role="alert" className="mt-2 text-sm text-error">
          {strings.read_story_error}
        </p>
      )}

      {upNext.length > 0 && (
        <div className="mt-12 w-full text-left">
          <h3 className="eyebrow mb-3">{strings.story_end_up_next}</h3>
          <StoryRows>
            {upNext.map((story) => (
              <StoryRow
                key={story.id}
                s={story}
                l={l}
                r={r}
                progress={progressByStoryId.get(story.id)}
              />
            ))}
          </StoryRows>
        </div>
      )}
      {loggedIn && (
        <Link
          to={
            storyPracticeCount > 0
              ? `/practice?story=${encodeURIComponent(storyId)}`
              : "/practice"
          }
          className="btn-secondary mt-4 w-full py-3 text-[15px]"
        >
          <PiCards size={18} className="text-primary" />
          {storyPracticeCount > 0
            ? strings.story_end_practice_story.replace(
                "{count}",
                String(storyPracticeCount),
              )
            : strings.story_end_practice}
        </Link>
      )}

      {loggedIn && <StoryRating l={l} />}
    </section>
  );
}
